import { Markdown } from '@/components/Markdown'
import type { ImplementedCheck, Role, Task } from '@/lib/types'

/**
 * What the builder implemented, alongside (never replacing) the expert's original rubric.
 *
 * The published map is keyed on the expert's own criterion ids, so a criterion no check
 * grades is shown as ungraded rather than being absent. That gap is the thing worth seeing:
 * a map listing only what was implemented cannot be told apart from a complete one.
 */
export function ImplementedRubric({ task, role, showNotes = true }: { task: Task; role: Role; showNotes?: boolean }) {
  const spec = task.authoring_submission?.handoff?.implemented_rubric
  if (!spec?.checks?.length) return showNotes ? <RubricNotes task={task} role={role} /> : null
  return (
    <section className="implemented-rubric">
      {showNotes && <RubricNotes task={task} role={role} />}
      <div className="implemented-heading"><h3>What the verifier checks</h3><span className="muted small">{spec.checks.length} checks</span></div>
      <ul className="implemented-checks">
        {spec.checks.map((check, index) => <Check key={check.id} check={check} index={index} />)}
      </ul>
    </section>
  )
}

/** Builder prose is developer-only; coverage gaps stay visible to every reviewer. */
export function RubricNotes({ task, role }: { task: Task; role: Role }) {
  const handoff = task.authoring_submission?.handoff
  const notes = role === 'engineer' ? handoff?.implementation_notes : undefined
  const spec = handoff?.implemented_rubric
  const linked = new Set(spec?.checks?.flatMap(check => check.rubric_criterion_ids))
  const retired = new Set(task.retired_criterion_ids ?? [])
  // Without a map, coverage is unknown rather than entirely missing.
  const unlinked = spec?.checks?.length ? (task.rubric ?? []).filter(
    criterion => !linked.has(criterion.criterion_id) && !retired.has(criterion.criterion_id),
  ) : []
  if (!notes && !unlinked.length) return null
  const title = notes ? 'Implementation notes' : 'Rubric coverage'
  return <section className="rubric-notes" aria-label={title}>
    <h3>{title}</h3>
    {notes && <Markdown source={notes} />}
    {unlinked.length > 0 && <div className="implemented-gap">
      <span>No verifier check linked:</span>
      <ul>{unlinked.map(criterion => <li key={criterion.criterion_id}>
        <code>{criterion.criterion_id}</code> {criterion.criterion}
      </li>)}</ul>
    </div>}
  </section>
}

function Check({ check, index }: { check: ImplementedCheck; index: number }) {
  return (
    <li data-rubric-check={check.id}>
      <span className="implemented-number">{index + 1}</span>
      <span className="criterion-body">
        <span className="implemented-label">{check.label}</span>
        <span className="check-meta">
          {check.required_for_pass && <span>Required to pass</span>}
          {check.origin === 'engineer' ? (
            <span className="muted small">added by the builder</span>
          ) : (
            check.rubric_criterion_ids.map((id) => (
              <span key={id}>{id}</span>
            ))
          )}
          <span className="muted small">{check.component} · weight {Number(check.weight.toPrecision(5))}</span>
        </span>
        {check.note && <span className="muted small">{check.note}</span>}
      </span>
    </li>
  )
}
